import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Achievement {
  title: string;
  description: string;
  date: string;
  rarity: 'legendary' | 'epic' | 'rare';
  icon: string;
  progress: number;
}

interface ProgressionShowcaseProps {
  achievements: Achievement[];
}

const ProgressionShowcase = ({ achievements }: ProgressionShowcaseProps) => {
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary':
        return 'from-blood-red to-accent';
      case 'epic':
        return 'from-frost-blue to-primary';
      case 'rare':
        return 'from-metallic-silver to-muted';
      default:
        return 'from-border to-muted';
    }
  };

  const getRarityBorder = (rarity: string) => {
    switch (rarity) {
      case 'legendary':
        return 'border-blood-red shadow-glow-red';
      case 'epic':
        return 'border-frost-blue shadow-glow-blue';
      case 'rare':
        return 'border-metallic-silver';
      default:
        return 'border-border';
    }
  };

  return (
    <section id="achievements" className="py-16 bg-deep-void">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-cinzel text-4xl font-bold text-foreground mb-4">
            Progression <span className="text-blood-red">Showcase</span>
          </h2>
          <p className="font-inter text-muted-foreground max-w-2xl mx-auto">
            Major milestones and rare achievements demonstrating dedication to mastery
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {achievements.map((achievement, index) => (
            <div
              key={index}
              className={`relative bg-card border-2 rounded-lg p-6 hover:scale-105 transition-all duration-300 ${getRarityBorder(achievement.rarity)}`}
            >
              {/* Rarity Gradient Background */}
              <div className={`absolute inset-0 opacity-10 bg-gradient-to-br ${getRarityColor(achievement.rarity)} rounded-lg`}></div>

              <div className="relative z-10">
                {/* Icon & Date */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg bg-gradient-to-br ${getRarityColor(achievement.rarity)}`}>
                    <Icon name={achievement.icon as any} size={28} className="text-white" />
                  </div>
                  <span className="text-xs font-rajdhani text-muted-foreground">{achievement.date}</span>
                </div>

                {/* Title */}
                <h3 className="font-cinzel text-xl font-bold text-foreground mb-2">{achievement.title}</h3>

                {/* Description */}
                <p className="font-inter text-sm text-muted-foreground mb-4">{achievement.description}</p>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-rajdhani">
                    <span className="text-muted-foreground">Completion</span>
                    <span className="text-foreground font-bold">{achievement.progress}%</span>
                  </div>
                  <div className="h-2 bg-surface rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${getRarityColor(achievement.rarity)} transition-all duration-500`}
                      style={{ width: `${achievement.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .shadow-glow-red {
          box-shadow: 0 0 20px rgba(204, 0, 0, 0.4);
        }
        .shadow-glow-blue {
          box-shadow: 0 0 20px rgba(0, 204, 255, 0.4);
        }
      `}</style>
    </section>
  );
};

export default ProgressionShowcase;